import {timestampDate} from '@github-ui/dotcom-schema/protobuf'
import type {
  FileMetadataFile,
  FileMetadataResponse,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/file_metadata'
import {ChangeType} from '@github-ui/dotcom-schema/types/pull_requests/v1/file_tree'
import type {PatchStatus} from '@github-ui/diff-file-helpers'
import {sendCustomMetric} from '@github-ui/stats'
import type {FilesRoutePayload} from '../payloads/files'

export type FileMetadataErrorReason =
  | 'comparison_mismatch'
  | 'duplicate_file'
  | 'missing_commit_created_at'
  | 'missing_file'
  | 'request_failed'
  | 'unsupported_change_type'

export class FileMetadataError extends Error {
  readonly reason: FileMetadataErrorReason

  constructor(message: string, reason: FileMetadataErrorReason, options?: ErrorOptions) {
    super(message, options)
    this.name = 'FileMetadataError'
    this.reason = reason
  }
}

export function reportFileMetadataError(reason: FileMetadataErrorReason) {
  sendCustomMetric({
    name: 'PULL_REQUESTS_FILES_CHANGED_FILE_METADATA_ERROR',
    value: 1,
    tags: {reason},
  })
}

interface MergeFileMetadataResult {
  routeData: FilesRoutePayload
  errorReasons: FileMetadataErrorReason[]
}

export function mergeFileMetadataIntoChangesData(
  routeData: FilesRoutePayload,
  metadata: FileMetadataResponse,
): MergeFileMetadataResult {
  const {baseOid, headOid} = routeData.pullRequest.comparison
  if (metadata.baseSha !== baseOid || metadata.headSha !== headOid) {
    throw new FileMetadataError('File metadata response does not match the changes comparison', 'comparison_mismatch')
  }

  const errorReasons: FileMetadataErrorReason[] = []
  const metadataByPath = buildMetadataByPath(metadata.files, errorReasons)

  return {
    errorReasons,
    routeData: {
      ...routeData,
      commits: metadata.commits.map(commit => {
        if (!commit.createdAt) {
          errorReasons.push('missing_commit_created_at')
        }

        return {
          actorLogin: commit.actorLogin,
          createdAt: commit.createdAt ? timestampDate(commit.createdAt).toISOString() : '',
          messageHeadline: commit.messageHeadline,
          oid: commit.oid,
          shortOid: commit.shortOid,
        }
      }),
      diffSummaries: routeData.diffSummaries.map(summary => {
        const fileMetadata = metadataByPath.get(summary.path)
        if (!fileMetadata) {
          errorReasons.push('missing_file')
          return summary
        }

        return {
          ...summary,
          changeType: toPatchStatus(fileMetadata.changeType, errorReasons),
          linesAdded: fileMetadata.linesAdded,
          linesChanged: fileMetadata.linesChanged,
          linesDeleted: fileMetadata.linesDeleted,
        }
      }),
    },
  }
}

function buildMetadataByPath(files: FileMetadataFile[], errorReasons: FileMetadataErrorReason[]) {
  const metadataByPath = new Map<string, FileMetadataFile>()
  const duplicatePaths = new Set<string>()

  for (const file of files) {
    if (duplicatePaths.has(file.path)) {
      continue
    }

    if (metadataByPath.has(file.path)) {
      errorReasons.push('duplicate_file')
      metadataByPath.delete(file.path)
      duplicatePaths.add(file.path)
      continue
    }

    metadataByPath.set(file.path, file)
  }

  return metadataByPath
}

function toPatchStatus(changeType: ChangeType, errorReasons: FileMetadataErrorReason[]): PatchStatus {
  switch (changeType) {
    case ChangeType.ADDED:
      return 'ADDED'
    case ChangeType.CHANGED:
      return 'CHANGED'
    case ChangeType.COPIED:
      return 'COPIED'
    case ChangeType.DELETED:
      return 'DELETED'
    case ChangeType.EMPTY:
      return 'EMPTY'
    case ChangeType.MODIFIED:
      return 'MODIFIED'
    case ChangeType.REMOVED:
      return 'REMOVED'
    case ChangeType.RENAMED:
      return 'RENAMED'
    default:
      errorReasons.push('unsupported_change_type')
      return 'UNKNOWN'
  }
}
